import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { Loader } from 'lucide-react';

const OpenChat = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { login } = useAuth();
    const { setCurrentWorkspace } = useWorkspace();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const handleAutoLogin = async () => {
            try {
                setLoading(true);
                setError(null);

                // Call API to validate token and get login credentials
                const response = await apiClient.get(`/api/auth/chat-access/${token}`);

                if (response.data.success) {
                    const { token: jwtToken, user, workspaceId, contactPhone } = response.data;

                    // Store token and user in localStorage
                    localStorage.setItem('token', jwtToken);
                    localStorage.setItem('user', JSON.stringify(user));
                    localStorage.setItem('currentWorkspaceId', workspaceId.toString());
                    apiClient.defaults.headers.common['Authorization'] = `Bearer ${jwtToken}`;
                    apiClient.defaults.headers.common['x-workspace-id'] = workspaceId.toString();

                    // Force a page reload to ensure auth context is updated
                    // The AuthContext will pick up the token from localStorage on reload
                    window.location.href = contactPhone
                        ? `/chats?contact=${encodeURIComponent(contactPhone)}`
                        : '/chats';
                    return;
                } else {
                    setError(response.data.message || 'Invalid access token');
                    toast.error(response.data.message || 'Invalid access token');
                }
            } catch (err) {
                const errorMessage = err.response?.data?.message || err.message || 'Failed to authenticate';
                setError(errorMessage);
                toast.error(errorMessage);
                console.error('Auto-login error:', err);

                // Redirect to login after 3 seconds
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } finally {
                setLoading(false);
            }
        };

        if (token) {
            handleAutoLogin();
        } else {
            setError('No access token provided');
            setLoading(false);
            setTimeout(() => navigate('/login'), 2000);
        }
    }, [token, navigate, login, setCurrentWorkspace]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
                <div className="text-center">
                    <Loader className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">Opening chat...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
                <div className="text-center max-w-md mx-auto p-6 bg-white rounded-lg shadow-lg">
                    <div className="text-red-600 text-6xl mb-4">⚠️</div>
                    <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
                    <p className="text-gray-600 mb-4">{error}</p>
                    <p className="text-sm text-gray-500">Redirecting to login page...</p>
                </div>
            </div>
        );
    }

    return null;
};

export default OpenChat;
